import { PlusIcon } from '@heroicons/react/24/outline';
import { Checkbox, FormLabel, Input } from '@mui/joy';

import { Dispatch, SetStateAction, useState } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { useCurrentOrganization, useListTags } from '@/hooks/useSwr';
import { ResourceTag } from '@/types/schema';
import { lowerCaseIncludes } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { SingleResourceTag } from './SingleResourceTag';

export function ResourceTagListSearch({
  tagsOnResource,
  setTagsOnResource,
  className,
  canManageTags,
}: {
  tagsOnResource: ResourceTag[];
  setTagsOnResource: Dispatch<SetStateAction<ResourceTag[]>>;
  className?: string;
  canManageTags: boolean;
}) {
  const [search, setSearch] = useState('');
  const { data: tags } = useListTags();

  const resourceTags =
    tags?.flatMap((tag) =>
      tag.values.map((tv) => ({
        tag_key: tag.key,
        tag_value: tv.value,
        tag_key_id: tag.id,
        tag_value_id: tv.id,
      }))
    ) ?? [];

  const filteredResourceTags = resourceTags.filter(
    (t) =>
      lowerCaseIncludes(t.tag_key, search) ||
      lowerCaseIncludes(t.tag_value, search)
  );

  return (
    <div
      className={cn(
        'flex max-h-[350px] flex-col items-start justify-start gap-2 overflow-hidden',
        className
      )}
    >
      <div className="w-full p-2 pb-0">
        <Input
          placeholder="Search tags"
          className="w-full rounded-md border border-secondary px-2 py-1.5"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="sm"
        />
      </div>
      <div className="flex w-full flex-col gap-2 overflow-y-auto overflow-x-hidden p-2 pt-0">
        {filteredResourceTags.map((t) => (
          <div className="relative flex w-full items-center justify-between">
            <SingleResourceTag
              key={t.tag_value_id}
              tag_key={t.tag_key}
              tag_value={t.tag_value}
              maxWidth={210}
            />
            {canManageTags && (
              <Checkbox
                data-testid={`${t.tag_key}-${t.tag_value}-checkbox`}
                checked={tagsOnResource.some(
                  (tor) =>
                    tor.tag_key_id === t.tag_key_id &&
                    tor.tag_value_id === t.tag_value_id
                )}
                onChange={(e) => {
                  if (e.target.checked) {
                    setTagsOnResource((prev) => [
                      ...prev,
                      {
                        tag_key: t.tag_key,
                        tag_value: t.tag_value,
                        tag_key_id: t.tag_key_id,
                        tag_value_id: t.tag_value_id,
                      },
                    ]);
                  } else {
                    setTagsOnResource((prev) =>
                      prev.filter(
                        (tor) =>
                          tor.tag_key_id !== t.tag_key_id ||
                          tor.tag_value_id !== t.tag_value_id
                      )
                    );
                  }
                }}
                overlay
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ResourceTagCrudPaneEditor({
  tagsOnResource,
  setTagsOnResource,
}: {
  tagsOnResource: ResourceTag[];
  setTagsOnResource: Dispatch<SetStateAction<ResourceTag[]>>;
}) {
  const { data: organization } = useCurrentOrganization();
  const isPersonal = organization?.is_personal;

  const { authorize } = usePermissions('workspace');
  const canManageTags = authorize('workspaces:manage');

  return (
    !isPersonal && (
      <>
        <FormLabel sx={{ marginBottom: 0.5 }}>Resource tags</FormLabel>
        <div className="relative flex min-h-12 flex-wrap items-center gap-2 rounded-lg border border-secondary p-2 pr-12 dark:border-tertiary dark:bg-black/40">
          {tagsOnResource.map((tag) => (
            <SingleResourceTag
              maxWidth={250}
              tag_key={tag.tag_key}
              tag_value={tag.tag_value}
              key={`${tag.tag_key}-${tag.tag_value}`}
              onRemove={() => {
                setTagsOnResource((prev) => {
                  return prev.filter(
                    (t) =>
                      t.tag_key_id !== tag.tag_key_id ||
                      t.tag_value_id !== tag.tag_value_id
                  );
                });
              }}
            />
          ))}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="absolute right-2 top-2 rounded-full border border-ls-black p-1.5"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" side="top" className="p-0">
              <ResourceTagListSearch
                tagsOnResource={tagsOnResource}
                setTagsOnResource={setTagsOnResource}
                canManageTags={canManageTags}
              />
            </PopoverContent>
          </Popover>
        </div>
      </>
    )
  );
}
